/**
 * useDataFetching
 *
 * Shared state for REST-backed list hooks (`useApiKeys`, `useUsers`): owns the
 * `items / loading / error` triad, the fetch-on-mount effect, `refetch`, and
 * the two common optimistic mutations — append-on-create and
 * remove-with-revert-on-delete. Entity-specific mutations (e.g. rotate, update
 * roles) live in the wrapping hook and operate on the exposed `setItems`.
 */

import { useState, useEffect, useCallback } from 'react';

export interface UseDataFetchingOptions<T, CreateInput, CreateResult> {
  /** Loads the full list. Called on mount and by `refetch`. */
  fetchFn: () => Promise<T[]>;
  /** Creates an item. The created entity is appended via `selectCreated`. */
  createFn?: (input: CreateInput) => Promise<CreateResult>;
  /**
   * Extracts the item to append from a create response. Use identity
   * (`(r) => r`) when the API returns the entity directly, or unwrap a wrapper
   * response (e.g. `(r) => r.key`). Required whenever `createFn` is supplied.
   */
  selectCreated?: (result: CreateResult) => T;
  /** Deletes an item by id. Drives the optimistic `remove`. */
  deleteFn?: (id: string) => Promise<void>;
  /** Identity accessor used to filter items during optimistic remove. */
  getItemId?: (item: T) => string;
}

export interface UseDataFetchingResult<T, CreateInput, CreateResult> {
  items: T[];
  setItems: React.Dispatch<React.SetStateAction<T[]>>;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  /** Present when `createFn` is supplied. Appends the created item. */
  create: (input: CreateInput) => Promise<CreateResult>;
  /** Present when `deleteFn` is supplied. Optimistic remove; reverts on error. */
  remove: (id: string) => Promise<void>;
}

export function useDataFetching<T, CreateInput = void, CreateResult = T>({
  fetchFn,
  createFn,
  selectCreated,
  deleteFn,
  getItemId,
}: UseDataFetchingOptions<T, CreateInput, CreateResult>): UseDataFetchingResult<
  T,
  CreateInput,
  CreateResult
> {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFn();
      setItems(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [fetchFn]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const create = useCallback(
    async (input: CreateInput): Promise<CreateResult> => {
      if (!createFn || !selectCreated) {
        throw new Error('useDataFetching: create called without createFn/selectCreated');
      }
      const result = await createFn(input);
      setItems((prev) => [...prev, selectCreated(result)]);
      return result;
    },
    [createFn, selectCreated]
  );

  const remove = useCallback(
    async (id: string): Promise<void> => {
      if (!deleteFn || !getItemId) {
        throw new Error('useDataFetching: remove called without deleteFn/getItemId');
      }
      const snapshot = items;
      setItems((prev) => prev.filter((item) => getItemId(item) !== id));
      try {
        await deleteFn(id);
      } catch (err) {
        setItems(snapshot);
        throw err;
      }
    },
    [deleteFn, getItemId, items]
  );

  return { items, setItems, loading, error, refetch, create, remove };
}
